import { useState, useRef, useCallback } from 'react';

const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;

export function useVoice() {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState(null);
  const isSupported = !!SR;

  const recRef = useRef(null);
  const timeoutRef = useRef(null);
  const transcriptRef = useRef('');

  const startListening = useCallback((onResult, fieldType = 'text') => {
    if (!isSupported) {
      setError('Voice input requires Chrome or Edge');
      return;
    }
    setError(null);
    setTranscript('');
    transcriptRef.current = '';

    const rec = new SR();
    rec.lang = 'en-US';
    rec.continuous = false;
    rec.interimResults = true;
    recRef.current = rec;

    rec.onstart = () => setIsListening(true);

    rec.onresult = (e) => {
      const t = Array.from(e.results).map(r => r[0].transcript).join('');
      transcriptRef.current = t;
      setTranscript(t);
    };

    rec.onend = () => {
      setIsListening(false);
      clearTimeout(timeoutRef.current);
      const final = transcriptRef.current.trim();

      if (final && onResult) {
        if (fieldType === 'keywords') {
          const parts = final.split(/,|\band\b/i).map(s => s.trim()).filter(Boolean);
          onResult(parts);
        } else {
          onResult(final);
        }
      }
    };

    rec.onerror = (e) => {
      setError(e.error === 'not-allowed' ? 'Microphone permission denied' : `Voice error: ${e.error}`);
      setIsListening(false);
    };

    rec.start();
    timeoutRef.current = setTimeout(() => rec.stop(), 6000);
  }, [isSupported]);

  const stopListening = useCallback(() => {
    clearTimeout(timeoutRef.current);
    recRef.current?.stop();
    setIsListening(false);
  }, []);

  return { isListening, transcript, startListening, stopListening, isSupported, error };
}
